import { HOME_WORKFLOW_SEARCH_PARAM } from "schemas/agentIntegration";
import {
  launchWorkflowIdSchema,
  workflowPublicInputsSchema,
} from "schemas/workflowLaunch";
import { z } from "zod";

const storageKey = "replo.workflow-continuation.v1";
const pendingSchema = z.object({
  workflowId: launchWorkflowIdSchema,
  token: z.uuid().nullable(),
  draftId: z.uuid().nullable(),
  inputs: workflowPublicInputsSchema.optional(),
  expiresAt: z.number(),
});
type PendingWorkflow = z.infer<typeof pendingSchema>;

export function readPendingWorkflow({
  unclaimedOnly = false,
}: { unclaimedOnly?: boolean } = {}) {
  try {
    const pending = pendingSchema.safeParse(
      JSON.parse(sessionStorage.getItem(storageKey) ?? "null"),
    );
    if (
      !pending.success ||
      pending.data.expiresAt <= Date.now() ||
      (unclaimedOnly && pending.data.draftId)
    ) {
      sessionStorage.removeItem(storageKey);
      return null;
    }
    return pending.data;
  } catch {
    return null;
  }
}
export function savePendingWorkflow(pending: PendingWorkflow) {
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(pending));
  } catch {
    // Blocked storage only loses the handoff across reloads; the page keeps the in-memory value.
  }
}
export function clearPendingWorkflow({ draftId }: { draftId?: string } = {}) {
  try {
    if (draftId && readPendingWorkflow()?.draftId !== draftId) {
      return;
    }
    sessionStorage.removeItem(storageKey);
  } catch {
    // Navigation must remain available when browser storage is blocked.
  }
}

export function capturePendingWorkflowFromUrl({
  hash,
  searchParams,
}: {
  hash: string;
  searchParams: URLSearchParams;
}) {
  const workflowId = launchWorkflowIdSchema.safeParse(
    searchParams.get("workflowId"),
  );
  if (!workflowId.success) {
    return null;
  }

  const rawToken =
    new URLSearchParams(hash.slice(1)).get("workflowToken") ??
    searchParams.get("token");
  const token = z.uuid().safeParse(rawToken);
  const existing = readPendingWorkflow();
  if (
    rawToken === null &&
    existing?.workflowId === workflowId.data &&
    !existing.draftId
  ) {
    return existing;
  }
  const value: PendingWorkflow = {
    workflowId: workflowId.data,
    token: token.success ? token.data : null,
    draftId: null,
    inputs:
      existing?.workflowId === workflowId.data &&
      existing.token === (token.success ? token.data : null)
        ? existing.inputs
        : undefined,
    expiresAt: Date.now() + 86400000,
  };
  savePendingWorkflow(value);
  return value;
}

export function getWorkflowSignupPath({
  osmanthus,
  pending,
}: {
  osmanthus: boolean;
  pending: PendingWorkflow;
}) {
  const continuationParams = new URLSearchParams({
    workflowId: pending.workflowId,
  });
  if (osmanthus) {
    continuationParams.set("osmanthus", "true");
  }
  const signupParams = new URLSearchParams({
    type: "agent",
    workflowId: pending.workflowId,
    next: `/agent/workflows/continue?${continuationParams.toString()}`,
  });
  if (osmanthus) {
    signupParams.set("osmanthus", "true");
  }
  const tokenHash = pending.token
    ? `#${new URLSearchParams({ workflowToken: pending.token }).toString()}`
    : "";
  return `/auth/signup?${signupParams.toString()}${tokenHash}`;
}

export function getPendingWorkflowHash(pathname: string) {
  const pending = readPendingWorkflow();
  if (pathname !== "/agent/workflows/continue" || !pending?.token) {
    return "";
  }
  return `#${new URLSearchParams({ workflowToken: pending.token }).toString()}`;
}

export function withPendingWorkflow(path: string) {
  const pending = readPendingWorkflow();
  if (!pending) {
    return path;
  }
  const url = new URL(path, window.location.origin);
  if (!pending.draftId) {
    const projectId = z.uuid().safeParse(url.pathname.split("/")[2]);
    return `/agent/workflows/continue${projectId.success ? `?projectId=${projectId.data}` : ""}`;
  }
  url.searchParams.delete(HOME_WORKFLOW_SEARCH_PARAM);
  url.searchParams.set("workflowDraft", pending.draftId);
  return `${url.pathname}${url.search}${url.hash}`;
}
